import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { DOMAIN } from "../../config";
import { useUser } from '../../contexts/userContext';
import CryptoJS from "crypto-js";
import LoginRegisterDialog from "./login";




/**
 * 图片路径（放在 /public/slot/）
 */
const PRIZES = [
  "/slot/cherry.png",
  "/slot/lemon.png",
  "/slot/watermelon.png",
  "/slot/star.png",
  "/slot/grapes.png",
  "/slot/seven.png",
];

const ITEM_HEIGHT = 80;
const VISIBLE_WIDTH = 80;

/**
 * SlotReel 单列滚动
 */
const SlotReel = forwardRef(({ onStop, initialSymbolIndex = 0 }, ref) => {
  const innerRef = useRef(null);
  const [items, setItems] = useState(PRIZES);


  useEffect(() => {
    if (innerRef.current) {
      innerRef.current.style.transform = `translateY(-${initialSymbolIndex * ITEM_HEIGHT
        }px)`;
    }
  }, []);

  useImperativeHandle(ref, () => ({
    spin(finalIndex, rounds = 6, duration = 2000) {
      if (!innerRef.current) return;

      // 长列表用于滚动动画
      const repeats = rounds + 2;
      const longList = [];
      for (let r = 0; r < repeats; r++) longList.push(...PRIZES);

      setItems(longList);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const targetIndex = rounds * PRIZES.length + finalIndex;
          const targetPx = -(targetIndex * ITEM_HEIGHT);

          innerRef.current.style.transition = `transform ${duration}ms cubic-bezier(0.33, 1, 0.68, 1)`;
          innerRef.current.style.transform = `translateY(${targetPx}px)`;

          const tidy = () => {
            setItems(PRIZES);
            setTimeout(() => {
              if (!innerRef.current) return;
              innerRef.current.style.transition = "none";
              innerRef.current.style.transform = `translateY(-${finalIndex * ITEM_HEIGHT
                }px)`;

              if (typeof onStop === "function") onStop();
            }, 40);
          };

          setTimeout(tidy, duration + 50);
        });
      });
    },
  }));

  return (
    <Box
      sx={{
        width: VISIBLE_WIDTH,
        height: ITEM_HEIGHT,
        border: "3px solid #1976d2",
        borderRadius: 2,
        overflow: "hidden",
        background: "#fff",
        boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
        display: "inline-block",
      }}
    >
      <Box ref={innerRef} sx={{ display: "block" }}>
        {items.map((src, idx) => (
          <Box
            key={idx}
            sx={{
              height: ITEM_HEIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={src}
              alt="icon"
              style={{
                width: 60,
                height: 60,
                objectFit: "contain",
                pointerEvents: "none",
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
});

/**
 * 主组件：LuckyWheelDialog
 * 用服务器结果控制
 */
export default function LuckyWheelDialog({ open, onClose }) {
  const reel1 = useRef(null);
  const reel2 = useRef(null);
  const reel3 = useRef(null);

  const [isRolling, setIsRolling] = useState(false);
  const [stoppedCount, setStoppedCount] = useState(0);
  const [message, setMessage] = useState("");
  const { userData, setUserData } = useUser();
  const [openLogin, setOpenLogin] = useState(false);

  useEffect(() => {
    
    if (stoppedCount === 3 && isRolling) {
      setIsRolling(false);
    }
  }, [stoppedCount]);

  /**
 * 🔥 从后端获取抽奖结果（带签名）
 */
  const rollFromServer = async () => {
    if (isRolling) return;
    
    if (!userData) {
      setOpenLogin(true)
      return
    }

    setMessage("");
    setStoppedCount(0);
    setIsRolling(true);

    try {
      const userId = userData.id;
      const apiKey = process.env.REACT_APP_API_KEY;
      const timestamp = Date.now().toString();

      // === 生成签名: HMAC_SHA256(userId + timestamp, apiKey) ===
      const raw = `${userId}:${timestamp}`;
      const sign = CryptoJS.HmacSHA256(raw, apiKey).toString();

      // 请求后端
      const res = await fetch(
        `${DOMAIN}lukcy/lottery?userId=${userId}&ts=${timestamp}&sign=${sign}`
      );
      const data = await res.json();
      console.log("Lottery Resp:", data);

      // 🔥 后端提示已签到
      if (data.code === 4004) {
        setIsRolling(false);
        setMessage("⭐ 您今天已经抽过奖啦，明天再来吧！");
        return;
      }

      // 后端其他错误
      if (data.code !== 0) {
        throw new Error(data.message || "Lottery API failed");
      }

      // ========= 抽奖逻辑 =========
      const { indices } = data.data;
      const [f0, f1, f2] = indices;

      // 播放动画
      reel1.current?.spin(f0, 5, 1800);
      setTimeout(() => reel2.current?.spin(f1, 6, 2400), 120);
      setTimeout(() => reel3.current?.spin(f2, 7, 3000), 240);

      // 结果展示 + 更新银币
      setTimeout(() => {
        let msg = "";
        let wonSilver = 0;

        if (f0 === 5 && f1 === 5 && f2 === 5) {
          msg = "🎉 恭喜获得头奖 70 个银币!";
          wonSilver = 70;
        } else if (f0 === f1 && f1 === f2) {
          msg = "🎉 恭喜获得二等奖 20 个银币!";
          wonSilver = 20;
        } else if (f0 === f1 || f1 === f2 || f0 === f2) {
          msg = "🎉 恭喜获得三等奖 2 个银币!";
          wonSilver = 2;
        } else {
          msg = "🙂 获得参与奖 1 个银币，加油！";
          wonSilver = 1;
        }

        setMessage(msg);
        setIsRolling(false);

        // 更新 userData.silver
        setUserData((prev) => ({
          ...prev,
          silver: (prev.silver || 0) + wonSilver
        }));
      }, 3200);

    } catch (err) {
      console.error(err);
      setMessage("抽奖失败，请稍后重试");
      setIsRolling(false);
    }
  };


  /**
   * 🔥 兑换会员（70 银币）带签名验证
   */
  const redeemVip = async () => {
    if (!userData) {
      setOpenLogin(true);
      return;
    }

    try {
      const userId = userData.id;
      const apiKey = process.env.REACT_APP_API_KEY;
      const timestamp = Date.now().toString();

      // 生成签名
      const raw = `${userId}:${timestamp}`;
      const sign = CryptoJS.HmacSHA256(raw, apiKey).toString();

      const res = await fetch(
        `${DOMAIN}lukcy/silverBuyVip?userId=${userId}&ts=${timestamp}&sign=${sign}`
      )
      const data = await res.json()
      console.log("Buy VIP Resp:", data)

      if (data.code !== 0) {
        alert(data.message || "兑换失败")
        return;
      }

      // 🔥 扣除 70 银币
      setUserData((prev) => ({
        ...prev,
        silver: (prev.silver ?? 0) - 70,
        vip: true, // 如果你有 vip 字段可以这样写，没有可以删掉
      }));

      alert("🎉 兑换成功，恭喜成为会员！刷新一下小程序你的会员数据就会更新出来了！")

    } catch (err) {
      console.error(err);
      alert("兑换失败，请稍后重试")
    }
  };


  return (
    <Box>
      <LoginRegisterDialog open={openLogin} onClose={() => setOpenLogin(false)} />
      <Dialog
        open={open}
        onClose={() => !isRolling && onClose()}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>幸运银币</DialogTitle>

        <DialogContent>
          {/* 当前银币 */}
          <Typography
            variant="body2"
            color="primary" // 可以换成 "secondary" 或自定义颜色
            sx={{ fontSize: "0.75rem", mb: 1 }}
          >
            您当前有 {userData?.silver ?? 0} 个银币
          </Typography>


          {/* 奖品说明 */}
          <Box sx={{ my: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: "0.75rem" }}>
              一等奖：三个幸运7（70个银币）
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: "0.75rem" }}>
              二等奖：三个水果或星星（20个银币）
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: "0.75rem" }}>
              三等奖：两个水果或星星（2个银币）
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: "0.75rem" }}>
              参与奖：三个都不一样（1个银币）
            </Typography>
          </Box>

          {/* 滚轮 */}
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, my: 2 }}>
            <SlotReel ref={reel1} onStop={() => setStoppedCount((s) => s + 1)} />
            <SlotReel ref={reel2} onStop={() => setStoppedCount((s) => s + 1)} />
            <SlotReel ref={reel3} onStop={() => setStoppedCount((s) => s + 1)} />
          </Box>

          {message && (
            <Typography
              align="center"
              variant="h6"
              color="primary"
              sx={{ mt: 1, fontSize: "0.8rem" }}
            >
              {message}
            </Typography>
          )}
        </DialogContent>


        <DialogActions sx={{ justifyContent: "center", pb: 3, flexDirection: "column", gap: 1 }}>
          <Button
            onClick={rollFromServer}
            variant="contained"
            disabled={isRolling}
            fullWidth
          >
            {isRolling ? "抽奖中..." : "开始抽奖"}
          </Button>

          <Button
            onClick={() => {
              if (isRolling) return;
              redeemVip(); // 兑换会员逻辑
            }}
            variant="outlined"
            disabled={isRolling || (userData?.silver ?? 0) < 70}
            fullWidth
          >
            兑换会员（70个银币）
          </Button>

          <Button
            onClick={() => !isRolling && onClose()}
            disabled={isRolling}
            fullWidth
          >
            关闭
          </Button>
        </DialogActions>


      </Dialog>
    </Box>
  );
}
